import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "@/api/adminOneClient";

/**
 * AdminOne™ Core-Satellite Intelligence Dashboard™ — Phase 38 (CEO-only).
 *
 * 8 widgets: Core Health · Satellite Activation · Top Active Satellites ·
 * New Satellites · Adoption · Retention · Core Utilization · Intelligence
 * Distribution.
 *
 * Truthfulness: all numbers are read from oracle_core_satellite_metrics
 * (TELEMETRY_VERIFIED). Zero fabricated counts.
 */

const C = {
  bg: "#0a1a14",
  emerald: "#10b981",
  gold: "#facc15",
  ivory: "#f5f5dc",
  ivoryD: "#d4d4aa",
  border: "rgba(16,185,129,0.25)",
};

const S = {
  page: { padding: "20px 24px", color: C.ivory, maxWidth: 1280, margin: "0 auto" },
  head: { display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 20 },
  h1: { fontFamily: "'Cinzel', serif", color: C.gold, fontSize: 26, margin: 0 },
  sub: { color: C.ivoryD, fontSize: 12, letterSpacing: 1, textTransform: "uppercase", marginTop: 4 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 },
  card: { background: "rgba(16,185,129,0.04)", border: `1px solid ${C.border}`, borderRadius: 8, padding: 14, minHeight: 120 },
  cardTitle: { color: C.gold, fontSize: 11, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6, fontWeight: 700 },
  cardValue: { color: C.ivory, fontSize: 28, fontWeight: 700, fontFamily: "'Cinzel', serif" },
  cardSub: { color: C.ivoryD, fontSize: 11, marginTop: 4 },
  refreshBtn: { background: C.gold, color: "#0a0a0a", border: "none", padding: "8px 14px", borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: "pointer" },
  list: { display: "flex", flexDirection: "column", gap: 4, marginTop: 6 },
  listRow: { display: "flex", justifyContent: "space-between", fontSize: 12, color: C.ivoryD, paddingBottom: 2, borderBottom: `1px solid rgba(245,245,220,0.05)` },
};

function Widget({ title, children, testid }) {
  return (
    <div style={S.card} data-testid={testid}>
      <div style={S.cardTitle}>{title}</div>
      {children}
    </div>
  );
}

export default function AdminOneCoreSatellitePage() {
  const [w, setW]         = useState(null);
  const [loading, setLoad] = useState(true);
  const [err, setErr]      = useState("");

  const load = useCallback(async () => {
    setLoad(true); setErr("");
    try {
      const { data } = await adminApi.coreSatelliteWidgets();
      setW(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "load_failed");
    } finally { setLoad(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div style={S.page} data-testid="adminone-core-satellite-page">
      <div style={S.head}>
        <div>
          <h1 style={S.h1}>Core-Satellite Intelligence Dashboard™</h1>
          <div style={S.sub}>Phase 38 · Telemetry-verified · CEO scope</div>
        </div>
        <button type="button" onClick={load} disabled={loading} style={S.refreshBtn} data-testid="csia-admin-refresh">
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {err && <div role="alert" style={{ color: "#fca5a5", padding: 10, marginBottom: 12, border: "1px solid rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.08)", borderRadius: 6 }}>{err}</div>}

      <div style={S.grid}>
        <Widget title="Core Health" testid="csia-w-core-health">
          <div style={S.cardValue}>{w?.core_health?.active_layers ?? "—"}/{w?.core_health?.total_layers ?? "—"}</div>
          <div style={S.cardSub}>{w?.core_health?.status || "—"}</div>
        </Widget>

        <Widget title="Satellite Activation" testid="csia-w-sat-activation">
          <div style={S.cardValue}>{w?.satellite_activation?.total ?? "—"}</div>
          <div style={S.cardSub}>
            unique users engaged: {w?.satellite_activation?.unique_users_engaged ?? "—"}
          </div>
        </Widget>

        <Widget title="Top Active Satellites" testid="csia-w-top-sats">
          {(w?.top_active_satellites || []).length === 0 && <div style={S.cardSub}>No activations yet.</div>}
          <div style={S.list}>
            {(w?.top_active_satellites || []).map((row) => (
              <div key={row.satellite_key} style={S.listRow}>
                <span>{row.satellite_key}</span>
                <span style={{ color: C.gold }}>{row.activations}</span>
              </div>
            ))}
          </div>
        </Widget>

        <Widget title="New Satellites (24h)" testid="csia-w-new-24h">
          <div style={S.cardValue}>{w?.new_satellites_24h ?? "—"}</div>
          <div style={S.cardSub}>activations in last 24 hours</div>
        </Widget>

        <Widget title="Satellite Adoption Rate" testid="csia-w-adoption">
          <div style={S.cardValue}>{w ? `${(w.satellite_adoption_rate * 100).toFixed(1)}%` : "—"}</div>
          <div style={S.cardSub}>(users with ≥1 activation) / total engaged</div>
        </Widget>

        <Widget title="Satellite Retention Rate" testid="csia-w-retention">
          <div style={S.cardValue}>{w ? `${(w.satellite_retention_rate * 100).toFixed(1)}%` : "—"}</div>
          <div style={S.cardSub}>(activations − deactivations) / activations</div>
        </Widget>

        <Widget title="Core Utilization" testid="csia-w-core-util">
          <div style={S.cardValue}>{w ? `${(w.core_utilization.ratio * 100).toFixed(1)}%` : "—"}</div>
          <div style={S.cardSub}>
            {w?.core_utilization?.core_state_views ?? 0} views · {w?.core_utilization?.core_users ?? 0} users
          </div>
        </Widget>

        <Widget title="Intelligence Distribution" testid="csia-w-distribution">
          <div style={S.list}>
            {Object.entries(w?.intelligence_distribution || {}).map(([cluster, count]) => (
              <div key={cluster} style={S.listRow}>
                <span>{cluster}</span>
                <span style={{ color: C.gold }}>{count}</span>
              </div>
            ))}
          </div>
        </Widget>
      </div>

      <div style={{ ...S.sub, marginTop: 24, textAlign: "center", opacity: 0.7 }}>
        TELEMETRY_VERIFIED · Source: oracle_core_satellite_metrics
      </div>
    </div>
  );
}
